import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  dateCellValueFormatter,
} from '~/components/SolaForceDataGrid';
import { Employees, useAllEmployees } from '~/data/employees';
import { RewardingClosedWorkflowsData } from '~/data/reports';
import { getFormattedEmploymentValue } from '~/scenes/PeopleModule/components/Tabs/BasicTab/components/EmploymentDataForm/data';
import { convertToDateOrUndefined } from '~/utils/helpers/date';

import { useReportNavigateToView } from '../../common';
import { DateHistoryCell } from '../../People/WorkflowReport/Common/components/HistoryCellsFormatter/DateHistoryCell';
import { HistoryCell } from '../../People/WorkflowReport/Common/components/HistoryCellsFormatter/DefaultHistoryCell';
import {
  RewardingClosedWorkflowsRow,
  WorkflowHistoryEntry,
  WorkflowReportHookOutput,
  WorkflowReportHookProps,
} from '../../People/WorkflowReport/types';

const groupByWorkflowUid = (data: RewardingClosedWorkflowsData[]) =>
  data.reduce((grouped, entry) => {
    const uid = entry.fOpenWorkflowUid;
    grouped[uid] = [...(grouped[uid] ?? []), entry];

    return grouped;
  }, {} as Record<string, RewardingClosedWorkflowsData[]>);

const buildWorkflowHistory = (
  entries: RewardingClosedWorkflowsData[],
  getEmployeeById: (employeeId?: number | undefined) => Employees | undefined,
): WorkflowHistoryEntry[] => {
  const sorted = [...entries].sort(
    (a, b) =>
      new Date(a.fOpenWorkflowLastUpdated).getTime() -
      new Date(b.fOpenWorkflowLastUpdated).getTime(),
  );

  return sorted.map((entry) => {
    const employee = getEmployeeById(entry.fOpenWorkflowUpdatedByEmpId);

    return {
      updatedAt: convertToDateOrUndefined(entry.fOpenWorkflowLastUpdated),
      status: getFormattedEmploymentValue(entry.fOpenWorkflowStatus, '_', ' '),
      approvedBy: employee
        ? `${employee.fEmpLastName}, ${employee.fEmpFirstName}`
        : '',
    };
  });
};

export const useClosedWorkflowRewardingReport = (
  props: WorkflowReportHookProps<RewardingClosedWorkflowsData>,
): WorkflowReportHookOutput<RewardingClosedWorkflowsRow> => {
  const { data, cellRenderState } = props;
  const { showHistory } = cellRenderState;
  const { t } = useTranslation();
  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();

  const rows = useMemo<RewardingClosedWorkflowsRow[]>(() => {
    const grouped = groupByWorkflowUid(data);

    return Object.values(grouped).flatMap((entries) => {
      const closedEntry = entries.find(
        (e) => e.fOpenWorkflowStatus === 'CLOSED',
      );
      if (!closedEntry) {
        return [];
      }

      const manager = getEmployeeById(closedEntry.fEmpManagerId);
      const managerName = manager
        ? `${manager.fEmpLastName}, ${manager.fEmpFirstName}`
        : '';

      const workflowHistory = showHistory
        ? buildWorkflowHistory(entries, getEmployeeById)
        : buildWorkflowHistory([closedEntry], getEmployeeById);

      return {
        employeeId: closedEntry.fEmpId,
        firstName: closedEntry.fEmpFirstName,
        lastName: closedEntry.fEmpLastName,
        employeeNumber: closedEntry.fEmpEmployeeNumber,
        organizationUnit: closedEntry.fEmpUnitId,
        unit: closedEntry.fEmpUnit,
        manager: managerName,
        organizationNumber: closedEntry.fEmpUnitNumber,
        type: getFormattedEmploymentValue(
          closedEntry.fOpenWorkflowType,
          '_',
          ' ',
        ),
        amount: closedEntry.fOpenWorkflowAmount,
        scheduledDate: convertToDateOrUndefined(
          closedEntry.fOpenWorkflowScheduledDate,
        ),
        info: closedEntry.fOpenWorkflowInfo,
        workflowHistory,
      };
    });
  }, [data, getEmployeeById, showHistory]);

  const columns = useMemo((): GridColDef<RewardingClosedWorkflowsRow>[] => {
    return [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => (
          <EmployeeCellComponent
            {...params}
            pictureOnly={true}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
        minWidth: 30,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => (
          <ButtonCellComponent
            {...params}
            onClick={() => goToEmployeeBasicDataView(params.row.employeeId)}
          />
        ),
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        minWidth: 130,
      },
      {
        field: 'organizationUnit',
        headerName: t('laOrgUnit'),
        minWidth: 150,
      },
      {
        field: 'unit',
        headerName: t('laUnit'),
        minWidth: 150,
      },
      {
        field: 'manager',
        headerName: t('laManager'),
        minWidth: 150,
      },
      {
        field: 'organizationNumber',
        headerName: t('laUnitNumber'),
        minWidth: 130,
      },
      {
        field: 'type',
        headerName: t('laType'),
        minWidth: 150,
      },
      {
        field: 'amount',
        headerName: t('laAmount'),
        minWidth: 100,
        type: 'number',
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        minWidth: 100,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="status"
          />
        ),
      },
      {
        field: 'approvedBy',
        headerName: t('laApprovedBy'),
        minWidth: 150,
        renderCell: (params) => (
          <HistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="approvedBy"
          />
        ),
      },
      {
        field: 'updated',
        headerName: t('Updated'),
        minWidth: 170,
        type: 'date',
        renderCell: (params) => (
          <DateHistoryCell
            workflowHistory={params.row.workflowHistory}
            showHistory={showHistory}
            fieldKey="updatedAt"
            format="DD/MM/YYYY HH:mm:ss"
          />
        ),
      },
      {
        field: 'scheduledDate',
        headerName: t('laScheduledDate'),
        minWidth: 150,
        type: 'date',
        valueFormatter: dateCellValueFormatter,
      },
      {
        field: 'info',
        headerName: t('laInfo'),
        minWidth: 150,
      },
    ];
  }, [t, goToEmployeeBasicDataView, showHistory]);

  return { rows, columns };
};
